import { ForgotPasswordIcon } from "@/components/bnp-icons";
import AuthWrapper from "@/components/layouts/AuthWrapper";
import { TextInput } from "@mantine/core";
import React, { useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { Button } from "@/components/bnp-ui";
import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { AuthService } from "@/services";
import parseHttpError from "@/utils/parseHttpError";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import ReCAPTCHA from "react-google-recaptcha";

const ResetPassword = () => {
  const RecaptchaComponent = ReCAPTCHA as unknown as React.FC<any>;
  const router = useRouter();
  const { token } = router.query;
  const email = token ? atob(token as string) : "";
  const recaptchaRef = useRef(null);
  const [recaptchaError, setRecaptchaError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    hasUpperCase: false,
    hasNumber: false,
    hasMinLength: false,
  });

  const defaultValues = {
    email: email,
    password: "",
    confirmPassword: "",
    recaptchaToken: "",
  };

  const validationSchema = yup.object().shape({
    password: yup
      .string()
      .required("Password is required")
      .min(8, "Password must be at least 8 characters"),
    confirmPassword: yup
      .string()
      .required("Please confirm your password")
      .oneOf([yup.ref("password")], "Passwords must match"),
    recaptchaToken: yup
      .string()
      .required("Please complete the reCAPTCHA verification"),
  });

  const EyeIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  const EyeOffIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  );

  const CheckIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 14C4.6862 14 2 11.3138 2 8C2 4.6862 4.6862 2 8 2C11.3138 2 14 4.6862 14 8C14 11.3138 11.3138 14 8 14ZM7.4018 10.4L11.6438 6.1574L10.7954 5.309L7.4018 8.7032L5.7044 7.0058L4.856 7.8542L7.4018 10.4Z"
        fill="#38C793"
      />
    </svg>
  );

  const XIcon = () => (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 14C4.6862 14 2 11.3138 2 8C2 4.6862 4.6862 2 8 2C11.3138 2 14 4.6862 14 8C14 11.3138 11.3138 14 8 14ZM8 7.1516L6.3032 5.4542L5.4542 6.3032L7.1516 8L5.4542 9.6968L6.3032 10.5458L8 8.8484L9.6968 10.5458L10.5458 9.6968L8.8484 8L10.5458 6.3032L9.6968 5.4542L8 7.1516Z"
        fill="#868C98"
      />
    </svg>
  );

  const checkPasswordStrength = (password: string) => {
    setPasswordStrength({
      hasUpperCase: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasMinLength: password.length >= 8,
    });
  };

  const {
    getValues,
    setValue,
    control,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
  });
  const handleRecaptchaChange = (value: any) => {
    setValue("recaptchaToken", value);
    setRecaptchaError("");
  };

  const resetPasswordRequest = async () => {
    try {
      console.log(getValues());
      const response = await AuthService.resetPassword({
        email: email,
        password: getValues().password,
      });
      return response.data;
    } catch (error: any) {
      console.log(error);
      throw new Error(parseHttpError(error).message);
    }
  };

  const mutation: any = useMutation({
    mutationFn: resetPasswordRequest,
    onSuccess: (res: any) => {
      toast.success("Your password has been reset!");

      setTimeout(() => {
        router.push(`/auth/sign-in`);
      }, 2000);
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to send verification code");
    },
  });

  const onSubmit = () => mutation.mutate();

  return (
    <AuthWrapper
      footerText={"Changed your mind?"}
      footerBtnText={"Go back"}
      footerLink={"/auth/sign-in"}
    >
      <div className="flex flex-col w-[100%]">
        <div className="flex flex-col items-center gap-4">
          <ForgotPasswordIcon />
          <div className="flex flex-col gap-1 items-center">
            <h2 className="text-main-900 font-semibold text-xl">
              Reset Password
            </h2>
            <p className="text-grey-7 text-sm font-normal">
              Enter and confirm your new password.
            </p>
          </div>
        </div>

        <div className=" w-[100%] mt-6">
          <form
            action={"submit"}
            className="flex flex-col gap-8"
            onSubmit={(e) => e.preventDefault()}
          >
            <Controller
              name="password"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  label="New Password"
                  type={showPassword ? "text" : "password"}
                  value={value}
                  className="w-[100%] h-[52px]"
                  size="md"
                  radius={"md"}
                  placeholder="Enter your new password"
                  onChange={(e) => {
                    onChange(e);
                    checkPasswordStrength(e.target.value);
                  }}
                  onBlur={onBlur}
                  rightSection={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                    >
                      {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  }
                  withAsterisk
                  error={errors?.password?.message as string}
                />
              )}
            />

            {/* Confirm Password Field */}

            <Controller
              name="confirmPassword"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <TextInput
                  label="Confirm Password"
                  type={showConfirmPassword ? "text" : "password"}
                  value={value}
                  className="w-[100%] h-[52px]"
                  size="md"
                  radius={"md"}
                  placeholder="Confirm your password"
                  onChange={onChange}
                  onBlur={onBlur}
                  rightSection={
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                    >
                      {showConfirmPassword ? <EyeOffIcon /> : <EyeIcon />}
                    </button>
                  }
                  withAsterisk
                  error={errors?.confirmPassword?.message as string}
                />
              )}
            />

            {/* Password Requirements */}
            <div className="mt-4">
              <p className="text-gray-700 mb-2">
                Password must contain at least:
              </p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {passwordStrength.hasUpperCase ? <CheckIcon /> : <XIcon />}
                  <span className="text-sm text-gray-600">
                    At least 1 uppercase
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {passwordStrength.hasNumber ? <CheckIcon /> : <XIcon />}
                  <span className="text-sm text-gray-600">
                    At least 1 number
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  {passwordStrength.hasMinLength ? <CheckIcon /> : <XIcon />}
                  <span className="text-sm text-gray-600">
                    At least 8 characters
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2">
              <RecaptchaComponent
                ref={recaptchaRef}
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY ?? ""}
                onChange={handleRecaptchaChange}
              />
              {(recaptchaError || errors.recaptchaToken) && (
                <span className="text-red-500 text-xs">
                  {recaptchaError || errors.recaptchaToken?.message}
                </span>
              )}
            </div>

            <Button
              className="bg-bnp-dark text-white text-sm font-extrabold w-[100%] h-[52px] rounded-lg mt-4 "
              isLoading={mutation.isPending}
              onClick={handleSubmit(onSubmit)}
            >
              Reset password
            </Button>
          </form>
        </div>
      </div>
    </AuthWrapper>
  );
};

export default ResetPassword;
